import { useState, useEffect, forwardRef, useImperativeHandle, ForwardedRef } from 'react';
import { Breakpoint, Container, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { useStores } from '../../../../stores';
import { Territory } from '../../../../domain/Territory';
import DesktopMyTerritoriesTableRow from './DesktopMyTerritoriesTableRow';
import { getShapeCenter, stringToLatLng } from '../../mapUtils';
import ReturnTerritoryDialog from '../../dialogs/ReturnTerritoryDialog';
import ShowTerritoryNotesDialog from '../../dialogs/ShowTerritoryNotesDialog';
import ShowTerritoryPublisherNotesDialog from '../../dialogs/ShowTerritoryPublisherNotesDialog';
import { useOpenDialog } from '../../../../hooks/useOpenDialog';
import { TerritoryAssignment } from '../../../../domain/TerritoryAssignment';
import { MyTerritoriesRef } from '../MyTerritoriesContent';

interface Props {
    size: Breakpoint;
}

const DesktopMyTerritoriesContent = observer(forwardRef<MyTerritoriesRef, Props>((props: Props, ref: ForwardedRef<MyTerritoriesRef>) => {
    const { t } = useTranslation();
    const { serverStore, territoryStore, publisherStore } = useStores();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const [myAssignments, setMyAssignments] = useState<TerritoryAssignment[]>([]);
    const [selectedTerritory, setSelectedTerritory] = useState<Territory | undefined>();
    const [selectedAssignment, setSelectedAssignment] = useState<TerritoryAssignment | undefined>();

    useEffect(() => {
        territoryStore.getMyTerritories((data) => {
            setMyAssignments(data)
        });
    }, [territoryStore, publisherStore.myPublisher]);

    useImperativeHandle(ref, () => ({
        refreshAssignments: () => {
            territoryStore.getMyTerritories((data) => {
                setMyAssignments(data)
            });
        }
    }));

    const handleTerritoryDirections = (territory: Territory) => {
        const shape = stringToLatLng(territory.boundary);
        const center = getShapeCenter(shape);
        window.open(`https://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=${center.lat},${center.lng}`);
    }

    const openReturnTerritoryDialog = (territory: Territory) => {
        setSelectedTerritory(territory);
        openDialog("return-territory")
    }

    const openTerritoryNotesDialog = (territory: Territory) => {
        setSelectedTerritory(territory);
        openDialog("territory-notes")
    }

    const openPublisherNotesDialog = (assignment: TerritoryAssignment) => {
        setSelectedAssignment(assignment);
        openDialog('territory-publisher-notes');
    }

    const handleCloseDialog = () => {
        closeDialog();
        setSelectedTerritory(undefined);
        setSelectedAssignment(undefined);
    }

    const handlePublisherNotesSave = (notes: string) => {
        if (!selectedAssignment) {
            return;
        }
        const updatedAssignment = { ...selectedAssignment, publisherNotes: notes };
        const { territory, ...assignmentPayload } = updatedAssignment as TerritoryAssignment & { territory?: Territory };
        territoryStore.updateTerritoryAssignment(assignmentPayload as TerritoryAssignment, () => {
            setMyAssignments(prev => prev.map(item => item.id === updatedAssignment.id ? { ...item, publisherNotes: notes } : item));
            handleCloseDialog();
        });
    }

    const handleTerritoryReturned = () => {
        territoryStore.getMyTerritories((data) => {
            setMyAssignments(data)
        });
    }

    return (
        <>
            <Container maxWidth={props.size} sx={{ mt: 4, mb: 4 }}>
                <Paper style={{ height: "80vh" }} sx={{ p: 2, display: 'flex', flexDirection: 'column' }} >
                    {myAssignments.length ? <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>{t("title")}</TableCell>
                                    <TableCell>{t("group")}</TableCell>
                                    <TableCell>{t("days_gone")}</TableCell>
                                    <TableCell>{t("actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {myAssignments
                                    .map((assignment: TerritoryAssignment) => (
                                        <DesktopMyTerritoriesTableRow
                                            key={assignment.id}
                                            assignment={assignment}
                                            onTerritoryDirections={handleTerritoryDirections}
                                            onTerritoryReturn={openReturnTerritoryDialog}
                                            onShowTerritoryNotes={openTerritoryNotesDialog}
                                            onShowPublisherNotes={openPublisherNotesDialog}
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer> : (
                        <>
                            {serverStore.serverRequestLoading && <LinearProgress />}
                            <Typography variant="h6" textAlign={'center'}>
                                {t("you_dont_have_assigned_territories")}
                            </Typography>
                        </>

                    )}
                </Paper>
            </Container>
            <ReturnTerritoryDialog
                territory={selectedTerritory!}
                isOpen={isOpen("return-territory")}
                onClose={handleCloseDialog}
                onTerritoryReturned={handleTerritoryReturned}
            />
            {selectedTerritory && (
                <ShowTerritoryNotesDialog
                    territory={selectedTerritory}
                    isOpen={isOpen('territory-notes')}
                    onClose={handleCloseDialog}
                />
            )}
            {selectedAssignment && (
                <ShowTerritoryPublisherNotesDialog
                    assignment={selectedAssignment}
                    isOpen={isOpen('territory-publisher-notes')}
                    onClose={handleCloseDialog}
                    onSave={handlePublisherNotesSave}
                />
            )}
        </>
    );
}));

export default DesktopMyTerritoriesContent;
